import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { ListClosingSoonTenders } from '@/modules/Tenders/services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useAuthStore from '@/stores/useAuthStore'
import React, { useState } from 'react'
import { Tender } from '@/modules/Tenders/types'
import { ResponseArray } from '@/types/shared'
import { useTranslation } from 'react-i18next'
import ClockIcons from '@/icons/ClockIcons'
import dayjs from 'dayjs'
import ExpandableAlertBanner from './ExpandableAlertBanner'
import TenderListItem from './TenderListItem'
import ScrollableList from './ScrollableList'

const ClosingSoon = () => {
    const { t } = useTranslation('tenders')
    const { employee } = useAuthStore()
    const [isExpanded, setIsExpanded] = useState(false)
    const [isDismissed, setIsDismissed] = useState(false)

    const { data: tendersData, renderState } = useQueryWithHandler<
        ResponseArray<Tender>
    >({
        queryFn: () => ListClosingSoonTenders(employee?.employee_id ?? 0),
        queryKey: [QUERY_KEYS.TENDERS_CLOSING_SOON],
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (isDismissed || !tendersData?.results?.length) {
        return null
    }

    const tenders = tendersData?.results ?? []
    const tenderCount = tenders.length

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsDismissed(true)
    }

    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded)
    }

    const handleTenderClick = (tender: Tender) => {
        if (tender.detail_url) {
            window.open(tender.detail_url, '_blank', 'noopener')
        }
    }

    const getDaysUntilClosing = (bidTime: string) => {
        const days = dayjs(bidTime).diff(dayjs(), 'day')
        return days
    }

    return (
        <div className="my-4">
            {tenderCount > 0 && (
                <ExpandableAlertBanner
                    icon={<ClockIcons className="w-6 h-6 text-orange-500" />}
                    title={t('closing_soon.title', {
                        count: tenderCount,
                    })}
                    description={t('closing_soon.description', {
                        count: tenderCount,
                    })}
                    isExpanded={isExpanded}
                    onToggleExpand={handleToggleExpand}
                    onClose={handleClose}
                    closeAriaLabel={t('closing_soon.close')}
                />
            )}

            {isExpanded && (
                <ScrollableList>
                    {tenders.map((tender) => {
                        const daysUntil = getDaysUntilClosing(tender.bid_time)
                        return (
                            <TenderListItem
                                key={tender.id}
                                tender={tender}
                                daysUntil={daysUntil}
                                onClick={handleTenderClick}
                            />
                        )
                    })}
                </ScrollableList>
            )}
        </div>
    )
}

export default ClosingSoon
